import { DEVTEST_API_BASE, API_BASE } from "../config";
import { Banner } from "../ui/primitives";
import { IconShield } from "../ui/icons";
import { ENVIRONMENT_MARKER } from "../buildInfo";

interface Props {
  compact?: boolean;
}

/** The console's standing environment declaration.
 *
 *  This is a governance surface, not decoration: the operator brief keeps
 *  "current lane", "current host / API base" and "dev/test only" on the
 *  must-stay-visible list, so neither variant may be collapsed behind a
 *  disclosure or reduced to a colour.
 *
 *  What the redesign changed is the FORM. `compact` used to be a paragraph of
 *  amber prose wrapping to three lines in a 240px sidebar; it is now a chip
 *  block that states the same three facts — environment, host, whether an
 *  override is active — in a scannable stack. The full variant loses its
 *  README cross-reference paragraph, which described a backend signer-policy
 *  dependency that has nothing to do with the environment the console is
 *  pointed at, and which no screen was reading. */
export function DevTestBanner({ compact = false }: Props) {
  const overridden = API_BASE !== DEVTEST_API_BASE;

  if (compact) {
    return (
      <div
        role="status"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "10px 11px",
          borderRadius: "var(--r-md)",
          border: "1px solid var(--accent-border)",
          background: "var(--accent-bg)",
          marginBottom: 4,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            color: "var(--accent)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.06,
            textTransform: "uppercase",
          }}
        >
          <IconShield size={13} />
          Dev / Test only
        </div>
        {/* WHICH environment. The console is deployed to DEV and to TEST from
            the same source with different build-time identities, talking to
            different issuers — an operator who cannot tell them apart at a
            glance can perform the right act on the wrong tier. It is also the
            one literal that differs between the two artifacts, so a deploy
            lane can prove a bundle was built for the environment it is being
            promoted into. */}
        <div style={{ fontSize: 11.5, color: "var(--fg-dim)", lineHeight: 1.4 }}>
          <code className="g-mono" style={{ fontSize: 10, padding: "1px 5px" }}>
            {ENVIRONMENT_MARKER}
          </code>
        </div>
        <div style={{ fontSize: 11.5, color: "var(--fg-dim)", lineHeight: 1.4 }}>
          No production data.
        </div>
        <code
          title={API_BASE}
          className="g-mono"
          style={{
            fontSize: 10,
            padding: "2px 6px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
        >
          {API_BASE.replace(/^https?:\/\//, "")}
        </code>
        {overridden && (
          <div style={{ fontSize: 10.5, color: "var(--warn)", fontWeight: 620 }}>
            Override active
          </div>
        )}
      </div>
    );
  }

  return (
    <Banner tone="accent" title="Dev / Test only — no production data">
      This console is bound to <code>{API_BASE}</code>. Production hosts are refused by the
      runtime config guard before a request can be built.
      {overridden && " An API base override is currently active."}
    </Banner>
  );
}
